import { useMemo } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { BrokerApi } from '@apply/api';
import { applicationInsights } from '../../../../../../ApplicationInsights';
import { createSearchDataSource } from '../functions/createSearchDataSource';

const brokerApi = new BrokerApi.BrokerApi(applicationInsights);

/** A broker as the search and "lean" endpoints return it, plus the text the dropdown shows. */
export interface Broker {
    id: number;
    businessName?: string;
    fullName?: string;
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    email?: string;
    phone?: string;
    displayName: string;
}

/** "Business - Full Name", or just the full name, as the old BrokerSelect showed it. */
const withDisplayName = (broker: Omit<Broker, 'displayName'>): Broker => ({
    ...broker,
    displayName: `${broker.businessName ? broker.businessName + ' - ' : ''}${broker.fullName}`,
});

const brokerQuery = (brokerId: number) =>
    queryOptions({
        queryKey: ['leanBroker', brokerId],
        queryFn: async (): Promise<Broker> => {
            // getLeanBroker isn't typed in the API
            const response = (await brokerApi.getLeanBroker(brokerId)) as {
                statusCode: number;
                result: Omit<Broker, 'displayName'>;
            };
            if (response.statusCode === 200) return withDisplayName(response.result);

            throw new Error('Failed to retrieve broker.');
        },
    });

/** The selected broker, for the details card. */
export const useBrokerQuery = (brokerId: number | undefined) =>
    useQuery({ ...brokerQuery(brokerId ?? 0), enabled: !!brokerId });

/** Searches brokers; the selected one comes from the same cache as the details card. */
export const useBrokerSearch = () => {
    const queryClient = useQueryClient();
    return useMemo(
        () =>
            createSearchDataSource<Broker>({
                key: 'id',
                search: async (searchText, page, pageSize) => {
                    const response = await brokerApi.search(searchText, page, pageSize);
                    // The API's type says one item, but it returns a page of them
                    const brokers = response.statusCode === 200 ? (response.result as unknown as Broker[]) : [];
                    return brokers.map(withDisplayName);
                },
                getByKey: (brokerId) => queryClient.ensureQueryData(brokerQuery(brokerId)),
            }),
        [queryClient]
    );
};
